// The provider stack (plan §3): theme, appearance, the API client for the served environment, TanStack Query, toasts
// and the router. main.tsx mounts it; the component tests mount the same stack over a fake transport.
import { useEffect, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, type RouterHistory } from '@tanstack/react-router';
import type { AuthClient, Client } from '@radish/api-client';
import { ThemeStyle, ToastProvider } from '@radish/ui-web';
import { ApiProvider } from '../data/api.tsx';
import type { BackofficeEnvironment } from '../env/environment.ts';
import { onSessionChange, sessionSnapshot } from '../session/session.ts';
import { AppearanceProvider } from '../shell/appearance.tsx';
import { ReportsSourceProvider, type ReportsSource } from '../features/reports/source.tsx';
import { WeatherFetchProvider, type WeatherFetch } from '../features/employees/weather.ts';
import { createAppRouter } from './router.tsx';
import { RefetchErrorToasts } from './refetch-errors.tsx';
import { StaleAssetRecovery } from './stale-assets.tsx';

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      // One fetch per filter change (Q-RPT-01 FIX): no refetch on focus, a short retry only for transient failures.
      queries: { refetchOnWindowFocus: false, staleTime: 15_000, retry: (count, error) => count < 2 && 'code' in error && error.code === 'network_error' },
    },
  });
}

/** What the session says, without its token: the user, where they act with which permissions, and what they may open. */
const scopeOf = (): string => JSON.stringify(sessionSnapshot()?.session ?? null);

/**
 * A sign-in, switch or sign-out drops every cached read and re-runs the route guards (an ended session returns to the
 * login page). A refresh that only renews the token, every nine minutes or so, changes neither: dropping the reads then
 * reset whatever a person had typed in an open editor (WF-006, seen on the dev stack). Subscribed while mounted, so an
 * unmounted app never reacts to another's session.
 */
function SessionSync({ router, queryClient, children }: { readonly router: ReturnType<typeof createAppRouter>; readonly queryClient: QueryClient; readonly children: ReactNode }) {
  useEffect(() => {
    let scope = scopeOf();
    return onSessionChange(() => {
      const next = scopeOf();
      if (next === scope) return;
      scope = next;
      queryClient.clear();
      void router.invalidate();
    });
  }, [router, queryClient]);
  return children;
}

interface AppRootProps {
  readonly environment: BackofficeEnvironment;
  readonly client?: Client;
  /** Tests: the client a review link's own token gets (the real one builds it from the environment). */
  readonly clientFor?: (bearer: string) => Client;
  readonly auth?: AuthClient;
  readonly reports?: ReportsSource;
  /** What the schedule's weather row fetches Open-Meteo with (the browser's fetch unless a test passes its own). */
  readonly weatherFetch?: WeatherFetch;
  readonly history?: RouterHistory;
  /** The router to mount (the component tests pass their own, so they can wait for its navigation to settle). */
  readonly router?: ReturnType<typeof createAppRouter>;
  readonly queryClient?: QueryClient;
}

export function createApp({ environment, client, clientFor, auth, reports, weatherFetch, history, router = createAppRouter(history), queryClient = createQueryClient() }: AppRootProps): ReactNode {
  const session = <SessionSync router={router} queryClient={queryClient}><RouterProvider router={router} /></SessionSync>;
  const routed = weatherFetch === undefined ? session : <WeatherFetchProvider value={weatherFetch}>{session}</WeatherFetchProvider>;
  return (
    <>
      <ThemeStyle />
      <StaleAssetRecovery />
      <AppearanceProvider>
        <ApiProvider environment={environment} {...(client === undefined ? {} : { client })} {...(clientFor === undefined ? {} : { clientFor })} {...(auth === undefined ? {} : { auth })}>
          <QueryClientProvider client={queryClient}>
            <ToastProvider><RefetchErrorToasts queryClient={queryClient} />{reports === undefined ? routed : <ReportsSourceProvider source={reports}>{routed}</ReportsSourceProvider>}</ToastProvider>
          </QueryClientProvider>
        </ApiProvider>
      </AppearanceProvider>
    </>
  );
}
